import { JwtTokenFullData } from 'multirpc-sdk';
import { createSelector } from 'reselect';

import { ACTION_CACHE_LONG } from 'modules/common/constants/const';
import { MultiService } from 'modules/api/MultiService';
import { TFAEndpointMeta, TFAQueryFnParams } from 'modules/tfa/types';
import { createQueryFnWithErrorHandler } from 'store/utils/createQueryFnWithErrorHandler';
import { createQuerySelectors } from 'store/utils/createQuerySelectors';
import { isTFAError } from 'modules/tfa/utils/isTFAError';
import { resolveJWTWithApiKey } from 'modules/common/utils/resolveJWTWithApiKey';
import { web3Api } from 'store/queries';

export interface IFetchOAuthAccountJWTParams
  extends TFAQueryFnParams<unknown> {}

const fetchOAuthAccountJWTFallback: JwtTokenFullData = {};

// The endpoint name is listed in endpointsSerializedByParams constant
// in packages/protocol/src/store/queries/index.ts file.
// If the name has changed it should be reflected there as well.
export const {
  endpoints: { fetchOAuthAccountJWT },
  useFetchOAuthAccountJWTQuery,
  useLazyFetchOAuthAccountJWTQuery,
} = web3Api.injectEndpoints({
  endpoints: build => ({
    fetchOAuthAccountJWT: build.query<
      JwtTokenFullData,
      IFetchOAuthAccountJWTParams
    >({
      keepUnusedDataFor: ACTION_CACHE_LONG, // using long cache in order to save tokens in store. refetch causes pages reload
      queryFn: createQueryFnWithErrorHandler({
        queryFn: async ({ totp }) => {
          const api = MultiService.getService().getAccountingGateway();

          const jwt = await api.getSyntheticJwtTokenWithApiKey(totp);

          const data = await resolveJWTWithApiKey(jwt);

          return { data };
        },
        errorHandler: error => {
          const meta: TFAEndpointMeta = {
            shouldSignOutOnTFAError: true,
          };

          return { error, meta };
        },
      }),
    }),
  }),
  overrideExisting: true,
});

export const {
  selectDataWithFallbackCachedByParams: selectOAuthAccountJWT,
  selectStateCachedByParams: selectOAuthAccountJWTState,
} = createQuerySelectors({
  endpoint: fetchOAuthAccountJWT,
  fallback: fetchOAuthAccountJWTFallback,
});

export const selectOAuthAccountJWTLoading = createSelector(
  selectOAuthAccountJWTState,
  ({ error, isLoading }) => isLoading || isTFAError(error),
);
